import { QuickLinks } from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  Action,
  AEMCard,
  AEMErrorMessage,
  AEMQuickLinks,
  Card,
  checkErrors,
  getEmptyContent,
  getGenericLabel,
  getImage,
  getLink,
  isDisplay,
  isError,
  isValidData,
  Status,
  UserSession
} from '@com-delta-idp/mnx-skymiles-shared';
import { ActionType, ImageSize } from '@com-delta-idp/mnx-ui-kit';
import { logError } from '@com-delta-idp/mnx-utils';
import { StatusTrackerResponse } from '../models/api/tracker.interfaces';
import {
  getCardTitle,
  getMembershipCardDetails,
  getSkyClubMembershipCard
} from '../utils/quicklinks.utils';

export const getQuickLinks = (
  aem: AEMQuickLinks | undefined,
  profile: UserSession | undefined,
  tracker: StatusTrackerResponse | undefined,
  featureFlag: boolean | undefined,
  errors: (string | undefined)[],
  aemErrorMessages: AEMErrorMessage[] | undefined
): QuickLinks => {
  try {
    if (checkErrors(errors, aemErrorMessages)) {
      return isError<QuickLinks>(
        'quick-links',
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }
    return isValidData<
      AEMQuickLinks,
      [AEMQuickLinks | undefined, UserSession | undefined, boolean | undefined]
    >('status', aem, profile, featureFlag)
      ? adaptQuickLinksObject(
          aem as AEMQuickLinks,
          profile as UserSession,
          tracker,
          featureFlag as boolean
        )
      : isDisplay<QuickLinks>(featureFlag);
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'QUICK_LINKS');
    return getEmptyContent<QuickLinks>('quick-links', aemErrorMessages, true);
  }
};
export const adaptQuickLinksObject = (
  aem: AEMQuickLinks,
  profile: UserSession,
  tracker: StatusTrackerResponse | undefined,
  featureFlag: boolean
) => {
  if (featureFlag) {
    const delta360CardIndex = aem.status.findIndex(
      (item: Status) => item.medallionId === 'Delta 360'
    );
    const cardIndex = profile.is360Tier
      ? delta360CardIndex
      : aem.status.findIndex(
          (item: Status) => item.medallionId === profile.membershipLevel
        );
    return {
      title: aem.title,
      cards: getCardsContent(aem, profile, cardIndex),
      membershipCard: getMembershipCardDetails(
        aem,
        profile,
        tracker,
        cardIndex
      ),
      disabled: !featureFlag,
      loaded: true
    };
  } else {
    return getEmptyContent<QuickLinks>('', [], true, true);
  }
};

const getCardsContent = (
  aem: AEMQuickLinks,
  profile: UserSession,
  cardIndex: number
): Card[] => {
  const cards: Card[] = [];
  aem.status[cardIndex].cards.forEach((quickLink: AEMCard) => {
    let result: Card = {
      id: quickLink.id,
      type: 'small' as ImageSize,
      content: {
        title: getCardTitle(aem, quickLink, profile)
      },
      image: getImage(quickLink.image.src || '', quickLink.image.alt || ''),
      action: getQuickLinkAction(aem, quickLink)
    };
    if (quickLink.id === 'skyMilesDigitalCard') {
      result.tag = getGenericLabel(
        aem.status[cardIndex].medallionLabel.toUpperCase(),
        `#${profile.smNumber}`
      );
    }
    result = getSkyClubMembershipCard(quickLink, result, profile, aem);
    cards.push(result);
  });
  return cards;
};

const getQuickLinkAction = (aem: AEMQuickLinks, quickLink: AEMCard): Action => {
  if (quickLink.id === 'skyMilesDigitalCard') {
    return {
      type: aem.action.type as ActionType,
      link: getLink(aem.action.link.label, aem.action.link.url)
    };
  }
  return {
    type: quickLink.actionType?.type as ActionType,
    link: getLink(
      quickLink.actionType?.link.label || '',
      quickLink.actionType?.link.url || ''
    )
  };
};
